/**
 * API client.
 *
 * One place that knows how to talk to the server: it attaches the access
 * token, refreshes it silently when it expires, and turns every failure into
 * an ApiError carrying the server's own message. Screens render that message
 * rather than inventing their own wording.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> = {},
    requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  get isConflict() {
    return this.status === 409;
  }
  get isPermissionDenied() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isOffline() {
    return this.status === 0;
  }
}

const BASE = "/api";

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;
let refreshInFlight: Promise<unknown | null> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function getAccessToken() {
  return accessToken;
}
export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

/** Refresh the session from the cookie, and hand back what the server said.
 *
 *  Collapse concurrent refreshes: several queries expiring together must not
 *  each rotate the refresh token, which would invalidate the others. Restoring
 *  a session on page load has to come through here too — it used to POST
 *  /auth/refresh by itself, which is a second rotation of the same cookie the
 *  moment anything else refreshes at the same time. React's StrictMode does
 *  exactly that on every load in development, and two tabs waking together do
 *  it in production.
 *
 *  Returns null when there is no session to restore, which is the ordinary
 *  answer for somebody who is simply not signed in. */
export async function refreshSession<T = unknown>(): Promise<T | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(`${BASE}/auth/refresh`, {
          method: "POST",
          credentials: "include",
        });
        if (!response.ok) return null;
        const body = await response.json();
        accessToken = body.access_token;
        return body;
      } catch {
        return null;
      } finally {
        setTimeout(() => (refreshInFlight = null), 0);
      }
    })();
  }
  return refreshInFlight as Promise<T | null>;
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  raw?: boolean;
  retryOnUnauthorized?: boolean;
};

export async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    method = "GET",
    body,
    params,
    signal,
    raw = false,
    retryOnUnauthorized = true,
  } = options;

  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }

  // FormData sets its own content-type, including the multipart boundary.
  // Setting it by hand produces a body the server cannot parse.
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const headers: Record<string, string> = {};
  if (body !== undefined && !isForm) headers["content-type"] = "application/json";
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      credentials: "include",
      signal,
    });
  } catch (error) {
    if ((error as Error).name === "AbortError") throw error;
    // A network failure is a state the interface must handle, not a crash.
    throw new ApiError(
      0,
      "offline",
      "You appear to be offline. Check your connection and try again.",
    );
  }

  if (response.status === 401 && retryOnUnauthorized) {
    const refreshed = await refreshSession();
    if (refreshed !== null) {
      return request<T>(path, { ...options, retryOnUnauthorized: false });
    }
    accessToken = null;
    onUnauthorized?.();
  }

  if (!response.ok) {
    let code = "error";
    let message = "Something went wrong. Please try again.";
    let details: Record<string, unknown> = {};
    let requestId: string | undefined;
    try {
      const payload = await response.json();
      if (payload?.error) {
        code = payload.error.code ?? code;
        message = payload.error.message ?? message;
        details = payload.error.details ?? {};
        requestId = payload.error.request_id;
      }
    } catch {
      // A non-JSON error body did not come from us. The one that actually
      // happens is nginx's own 413 page, and "something went wrong on our
      // side" sends an administrator looking for a fault that is not there.
      if (response.status === 413) {
        code = "too_large";
        message = "That file is too large to upload. Try one under 12 MB.";
      }
    }
    throw new ApiError(response.status, code, message, details, requestId);
  }

  if (raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, params?: RequestOptions["params"]) =>
    request<T>(path, { params }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: "DELETE", body }),
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<T>(path, { method: "POST", body: form });
  },
};

/** Download a CSV the server generates, without leaving the page. */
export async function download(path: string, params: RequestOptions["params"]) {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }
  const response = await fetch(url.toString(), {
    headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
    credentials: "include",
  });
  if (!response.ok) throw new ApiError(response.status, "export_failed", "The export could not be produced.");
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download =
    response.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ??
    "alpharoom-export.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}
